// Check if we're in Touch 'n Go environment
const isTouchNGoEnvironment = () => {
  return typeof my !== "undefined" && (my.getStorage || my.getStorageSync);
};

// Check if we're in a browser environment
const isBrowserEnvironment = () => {
  return typeof window !== "undefined" && window.sessionStorage;
};

export const getSessionStorageAsync = async key => {
  // console.log("Getting session storage for key:", key);

  if (!key) return null;

  try {
    // Try Touch 'n Go mini-program async storage first
    if (isTouchNGoEnvironment() && my.getStorage) {
      // console.log("Using Touch 'n Go async storage");
      return new Promise((resolve, reject) => {
        my.getStorage({
          key,
          success: res => {
            // console.log("Storage get success:", res);
            resolve(res.data);
          },
          fail: error => {
            console.error("Storage get failed:", error);
            resolve(null); // Return null instead of rejecting
          },
        });
      });
    }

    // Fallback to Touch 'n Go sync storage
    if (isTouchNGoEnvironment() && my.getStorageSync) {
      // console.log("Using Touch 'n Go sync storage");
      const storage = my.getStorageSync({ key });
      return storage?.data;
    }

    // Fallback to browser sessionStorage
    if (isBrowserEnvironment()) {
      // console.log("Using browser sessionStorage");
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }

    // Fallback to localStorage if sessionStorage not available
    if (typeof window !== "undefined" && window.localStorage) {
      // console.log("Using browser localStorage");
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }

    console.warn("No storage mechanism available");
    return null;
  } catch (error) {
    console.error("Error getting session storage:", error);
    return null;
  }
};

export const setSessionStorageAsync = async (key, value) => {
  if (!key) return false;

  // console.log("Setting session storage for key:", key, "value:", value);

  try {
    // Try Touch 'n Go mini-program async storage first
    if (isTouchNGoEnvironment() && my.setStorage) {
      // console.log("Using Touch 'n Go async storage");
      return new Promise((resolve, reject) => {
        my.setStorage({
          key,
          data: value,
          success: res => {
            // console.log("Storage set success:", res);
            resolve(true);
          },
          fail: error => {
            console.error("Storage set failed:", error);
            resolve(false); // Return false instead of rejecting
          },
        });
      });
    }

    // Fallback to Touch 'n Go sync storage
    if (isTouchNGoEnvironment() && my.setStorageSync) {
      // console.log("Using Touch 'n Go sync storage");
      my.setStorageSync({ key, data: value });
      return true;
    }

    // Fallback to browser sessionStorage
    if (isBrowserEnvironment()) {
      // console.log("Using browser sessionStorage");
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    }

    // Fallback to localStorage if sessionStorage not available
    if (typeof window !== "undefined" && window.localStorage) {
      // console.log("Using browser localStorage");
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    }

    console.warn("No storage mechanism available");
    return false;
  } catch (error) {
    console.error("Error setting session storage:", error);
    return false;
  }
};

export const removeSessionStorage = async key => {
  if (!key) return false;

  // console.log("Removing session storage for key:", key);

  try {
    // Try Touch 'n Go mini-program async storage first
    if (isTouchNGoEnvironment() && my.removeStorage) {
      // console.log("Using Touch 'n Go async storage");
      return new Promise((resolve, reject) => {
        my.removeStorage({
          key,
          success: res => {
            // console.log("Storage remove success:", res);
            resolve(true);
          },
          fail: error => {
            console.error("Storage remove failed:", error);
            resolve(false); // Return false instead of rejecting
          },
        });
      });
    }

    // Fallback to Touch 'n Go sync storage
    if (isTouchNGoEnvironment() && my.removeStorageSync) {
      // console.log("Using Touch 'n Go sync storage");
      my.removeStorageSync({ key });
      return true;
    }

    // Fallback to browser sessionStorage
    if (isBrowserEnvironment()) {
      // console.log("Using browser sessionStorage");
      window.sessionStorage.removeItem(key);
      return true;
    }

    // Fallback to localStorage if sessionStorage not available
    if (typeof window !== "undefined" && window.localStorage) {
      // console.log("Using browser localStorage");
      window.localStorage.removeItem(key);
      return true;
    }

    console.warn("No storage mechanism available");
    return false;
  } catch (error) {
    console.error("Error removing session storage:", error);
    return false;
  }
};

// Utility functions for easier usage
export const getSessionStorageSync = key => {
  if (!key) return null;

  try {
    // Try Touch 'n Go mini-program sync storage first
    if (isTouchNGoEnvironment() && my.getStorageSync) {
      const storage = my.getStorageSync({ key });
      return storage?.data;
    }

    // Fallback to browser sessionStorage
    if (isBrowserEnvironment()) {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }

    // Fallback to localStorage if sessionStorage not available
    if (typeof window !== "undefined" && window.localStorage) {
      // console.log("Using browser localStorage");
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }

    return null;
  } catch (error) {
    console.error("Error getting session storage sync:", error);
    return null;
  }
};

export const setSessionStorageSync = (key, value) => {
  if (!key) return false;

  try {
    // Try Touch 'n Go mini-program sync storage first
    if (isTouchNGoEnvironment() && my.setStorageSync) {
      my.setStorageSync({ key, data: value });
      return true;
    }

    // Fallback to browser sessionStorage
    if (isBrowserEnvironment()) {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    }

    // Fallback to localStorage if sessionStorage not available
    if (typeof window !== "undefined" && window.localStorage) {
      // console.log("Using browser localStorage");
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error setting session storage sync:", error);
    return false;
  }
};

// Async usage
export const getSessionStorage = async key => {
  const data = await getSessionStorageAsync(key);
  // console.log("getSessionStorage: ", key, data);
  return data;
};

export const setSessionStorage = async (key, value) => {
  const success = await setSessionStorageAsync(key, value);
  if (success) {
    console.log("Data saved successfully");
  }
};
